import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Hourglass, MessageCircle, Plus, UserCheck, PhoneCall, X, Trash2, Undo2, ExternalLink } from 'lucide-react';
import {
  WaitlistRow, getClassWaitlist, addToWaitlist, updateWaitlist, enrollFromWaitlist, removeFromWaitlist,
} from '../api';
import { listMembers, MemberRow } from '../../members/api';
import PersonAvatar from '../../../components/PersonAvatar';
import { getWhatsAppLink } from '../../../utils/whatsapp';
import { useToast } from '../../../context/ToastContext';

/** Un teléfono de verdad tiene al menos 7 dígitos: el ETL de Kydemy dejó «52» (solo la lada) en varias fichas. */
export function telValido(p: string | null | undefined): p is string {
  return !!p && p.replace(/\D/g, '').length >= 7;
}

export function daysWaiting(start: string | null): number {
  if (!start) return 0;
  const ms = Date.now() - new Date(`${start.slice(0, 10)}T12:00:00`).getTime();
  return Math.max(0, Math.floor(ms / 86400000));
}

export const STATUS_META: Record<string, { label: string; cls: string }> = {
  waiting:   { label: 'En espera',  cls: 'bg-amber-500/15 text-amber-600 dark:text-amber-400' },
  contacted: { label: 'Contactado', cls: 'bg-sky-500/15 text-sky-600 dark:text-sky-400' },
  enrolled:  { label: 'Inscrito',   cls: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400' },
  discarded: { label: 'Descartado', cls: 'bg-surface-2 text-content-3' },
};

/** Fila de acciones compartida entre el panel por clase y la vista global. */
export function WaitlistActions({ row, onChanged }: { row: WaitlistRow; onChanged: () => void }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const act = async (fn: () => Promise<unknown>, okMsg: string) => {
    setBusy(true);
    try { await fn(); toast.success(okMsg); onChanged(); }
    catch (e: any) { toast.error(e?.message || 'No se pudo'); }
    finally { setBusy(false); }
  };
  const waLink = getWhatsAppLink(null, row.phone, `Hola ${row.first_name}, te escribimos de Let's Move 💃 sobre tu lugar en la clase de ${row.class_name}.`);
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {waLink && (
        <a href={waLink} target="_blank" rel="noreferrer" title="WhatsApp"
          className="flex items-center gap-1 rounded-lg bg-emerald-600 px-2 py-1 text-xs font-medium text-white transition hover:bg-emerald-700">
          <MessageCircle size={13} /> WhatsApp
        </a>
      )}
      {row.status === 'waiting' && (
        <button disabled={busy} onClick={() => act(() => updateWaitlist(row.id, { status: 'contacted' }), 'Marcado como contactado')}
          className="flex items-center gap-1 rounded-lg border border-line px-2 py-1 text-xs text-content-2 transition hover:bg-surface-2 disabled:opacity-40">
          <PhoneCall size={13} /> Contactado
        </button>
      )}
      {(row.status === 'contacted' || row.status === 'discarded') && (
        <button disabled={busy} onClick={() => act(() => updateWaitlist(row.id, { status: 'waiting' }), 'Regresado a "En espera"')}
          title="Deshacer: regresar a En espera"
          className="flex items-center gap-1 rounded-lg border border-line px-2 py-1 text-xs text-content-2 transition hover:bg-surface-2 disabled:opacity-40">
          <Undo2 size={13} /> En espera
        </button>
      )}
      {(row.status === 'waiting' || row.status === 'contacted') && (
        <>
          <button disabled={busy} onClick={() => act(() => enrollFromWaitlist(row.id), `${row.first_name} inscrito en ${row.class_name}`)}
            className="flex items-center gap-1 rounded-lg border border-line px-2 py-1 text-xs text-content-2 transition hover:bg-surface-2 disabled:opacity-40">
            <UserCheck size={13} /> Inscribir
          </button>
          <button disabled={busy} onClick={() => act(() => updateWaitlist(row.id, { status: 'discarded' }), 'Descartado')}
            className="flex items-center gap-1 rounded-lg border border-line px-2 py-1 text-xs text-content-3 transition hover:bg-surface-2 disabled:opacity-40">
            <X size={13} /> Descartar
          </button>
        </>
      )}
      {!row.from_kydemy && row.status === 'discarded' && (
        <button disabled={busy} onClick={() => { if (confirm('¿Quitar definitivamente de la lista?')) act(() => removeFromWaitlist(row.id), 'Eliminado de la lista'); }}
          className="flex items-center gap-1 rounded-lg border border-line px-2 py-1 text-xs text-content-3 transition hover:bg-surface-2 disabled:opacity-40">
          <Trash2 size={13} /> Borrar
        </button>
      )}
    </div>
  );
}

/** Alta a la lista: busca alumno existente o captura prospecto nuevo (nombre + tel). */
export function AddForm({ classId, onDone, onCancel }: { classId: number; onDone: () => void; onCancel: () => void }) {
  const toast = useToast();
  const [q, setQ] = useState('');
  const [results, setResults] = useState<MemberRow[]>([]);
  const [picked, setPicked] = useState<MemberRow | null>(null);
  const [phone, setPhone] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (picked || q.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(() => {
      // Alumnas (activas e inactivas) Y prospectos (Mabel 31-oct-2026): `status=all` deja fuera a los
      // prospectos, así que a quien ya estaba dado de alta como prospecto no se le podía «jalar» a la
      // lista y se acababa creando otra ficha repetida. Dos consultas, sin repetidos, alumnas primero.
      Promise.all([
        listMembers({ search: q.trim(), status: 'all', page_size: 6 }).then((r) => r.data).catch(() => [] as MemberRow[]),
        listMembers({ search: q.trim(), status: 'lead', page_size: 6 }).then((r) => r.data).catch(() => [] as MemberRow[]),
      ]).then(([alumnas, prospectos]) => {
        const vistos = new Set(alumnas.map((m) => m.account_id));
        const leads = prospectos.filter((m) => !vistos.has(m.account_id)).map((m) => ({ ...m, persona_tipo: m.persona_tipo || 'prospecto' }));
        setResults([...alumnas, ...leads].slice(0, 8));
      });
    }, 250);
    return () => clearTimeout(t);
  }, [q, picked]);
  const telDe = (m: MemberRow) => telValido(m.phone_number) ? `${m.phone_prefix ? `+${m.phone_prefix} ` : ''}${m.phone_number}` : null;

  async function save() {
    setBusy(true);
    try {
      if (picked) await addToWaitlist(classId, { contact_id: picked.account_id, note: note.trim() || undefined });
      else {
        const parts = q.trim().split(/\s+/);
        if (!parts[0]) { toast.error('Escribe el nombre'); setBusy(false); return; }
        await addToWaitlist(classId, {
          firstname: parts[0], lastname: parts.slice(1).join(' ') || undefined,
          phone: phone.trim() || undefined, note: note.trim() || undefined,
        });
      }
      toast.success('Agregado a la lista de espera');
      onDone();
    } catch (e: any) { toast.error(e?.message || 'No se pudo agregar'); }
    finally { setBusy(false); }
  }

  return (
    <div className="mt-3 rounded-xl border border-line bg-canvas p-3">
      <div className="relative">
        <input value={picked ? `${picked.firstname} ${picked.lastname || ''}` : q}
          onChange={(e) => { setPicked(null); setQ(e.target.value); }}
          placeholder="Nombre (busca alumno o escribe prospecto nuevo)" autoFocus
          className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-content outline-none focus:border-accent" />
        {/* .glass-menu: opaco. Con solo bg-surface el vidrio deja leer la fila de abajo. */}
        {results.length > 0 && !picked && (
          <div className="glass-menu absolute z-20 mt-1 w-full overflow-hidden rounded-lg border border-line shadow-xl">
            {results.map((m) => (
              <button key={m.account_id} onClick={() => { setPicked(m); setResults([]); }}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-content transition hover:bg-surface-2">
                <PersonAvatar contactId={m.account_id} hasPhoto={m.has_photo}
                  name={m.firstname} lastName={m.lastname} size="h-6 w-6" initialClass="text-[10px]" />
                <span className="min-w-0 flex-1 truncate">
                  {m.firstname} {m.lastname}
                  {m.persona_tipo && m.persona_tipo !== 'alumna' && <span className="ml-1.5 rounded-full bg-surface-2 px-1.5 py-0.5 text-[10px] text-content-3">{m.persona_tipo === 'prospecto' ? 'Prospecto' : m.persona_tipo === 'ex_alumna' ? 'Ex-alumna' : 'Visitante'}</span>}
                  {!m.persona_tipo && m.active === 0 && <span className="ml-1.5 text-xs text-content-3">(inactiva)</span>}
                </span>
                {/* El teléfono a la vista: Mabel (31-oct) no podía distinguir a quién estaba eligiendo. */}
                {telDe(m) && <span className="shrink-0 text-xs text-content-3">{telDe(m)}</span>}
              </button>
            ))}
          </div>
        )}
      </div>
      {picked && (
        <p className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-content-3">
          <span>{telDe(picked) ? `Tel. ${telDe(picked)}` : 'Sin teléfono en su ficha'}</span>
          <Link to={`/alumnos/${picked.account_id}`} target="_blank" className="inline-flex items-center gap-1 text-accent-ink hover:underline">
            <ExternalLink size={11} /> Ver ficha
          </Link>
        </p>
      )}
      {!picked && (
        <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Teléfono (opcional, para WhatsApp)"
          className="mt-2 w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-content outline-none focus:border-accent" />
      )}
      <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Nota (opcional): ej. preguntó por horario de tarde"
        className="mt-2 w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-content outline-none focus:border-accent" />
      <div className="mt-2 flex gap-2">
        <button disabled={busy} onClick={save}
          className="rounded-lg bg-accent-solid px-3 py-1.5 text-sm font-semibold text-on-accent transition hover:opacity-90 disabled:opacity-40">
          Agregar
        </button>
        <button onClick={onCancel} className="rounded-lg border border-line px-3 py-1.5 text-sm text-content-2 transition hover:bg-surface-2">Cancelar</button>
      </div>
      {!picked && q.trim().length >= 2 && (
        <p className="mt-1.5 text-xs text-content-3">Si no aparece en la búsqueda, se creará como prospecto nuevo.</p>
      )}
    </div>
  );
}

/** Bloque "Lista de espera" de la ficha de clase. `hideTitle`: vive dentro de una pestaña que
 *  ya se llama así (repetir el encabezado solo gastaba renglón). */
export default function WaitlistPanel({ classId, hideTitle = false, onChanged }: { classId: number; hideTitle?: boolean; onChanged?: () => void }) {
  const [rows, setRows] = useState<WaitlistRow[]>([]);
  const [adding, setAdding] = useState(false);
  const load = useCallback(() => { getClassWaitlist(classId).then((r) => setRows(r.data)).catch(() => setRows([])); }, [classId]);
  useEffect(() => { load(); }, [load]);
  // Tras cada acción se avisa al padre: inscribir a alguien mueve los contadores de las pestañas.
  const recargar = () => { load(); onChanged?.(); };

  const active = rows.filter((r) => r.status === 'waiting' || r.status === 'contacted');
  const resolved = rows.filter((r) => r.status === 'enrolled' || r.status === 'discarded');

  return (
    // Sin margen superior: su único uso (ficha de clase) ya lo enmarca en una tarjeta propia.
    <div>
      <div className={`flex items-center ${hideTitle ? 'justify-end' : 'justify-between'}`}>
        {!hideTitle && (
          <h3 className="flex items-center gap-2 text-sm font-semibold text-content">
            <Hourglass size={15} className="text-accent-ink" /> Lista de espera {active.length > 0 && <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] font-semibold text-amber-600 dark:text-amber-400">{active.length}</span>}
          </h3>
        )}
        {!adding && (
          <button onClick={() => setAdding(true)}
            className="flex items-center gap-1 rounded-lg border border-line px-2.5 py-1 text-xs font-medium text-content-2 transition hover:bg-surface-2">
            <Plus size={13} /> Agregar
          </button>
        )}
      </div>
      {adding && <AddForm classId={classId} onDone={() => { setAdding(false); recargar(); }} onCancel={() => setAdding(false)} />}
      {rows.length === 0 && !adding && (
        <p className="mt-2 text-sm text-content-3">Nadie en espera. Cuando alguien pregunte por esta clase, agrégala aquí para no perderle la pista.</p>
      )}
      <div className="mt-2 space-y-1.5">
        {[...active, ...resolved].map((r) => (
          <div key={r.id} className="flex flex-wrap items-center gap-3 rounded-lg border border-line bg-canvas px-3 py-2">
            <PersonAvatar contactId={r.contact_id} hasPhoto={r.has_photo}
              name={r.first_name} lastName={r.last_name} size="h-8 w-8" ringClass="fill-canvas" />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2 text-sm">
                {/* El nombre abre la ficha (igual que en /prospectos): Mabel (31-oct-2026) necesitaba ver el
                    teléfono de quien espera y aquí el nombre era texto plano. */}
                <Link to={`/alumnos/${r.contact_id}`} className="font-medium text-content hover:underline">{r.first_name} {r.last_name || ''}</Link>
                <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${STATUS_META[r.status].cls}`}>{STATUS_META[r.status].label}</span>
                {(r.status === 'waiting' || r.status === 'contacted') && (
                  <span className="text-xs text-content-3">esperando {daysWaiting(r.start_date)} días</span>
                )}
              </div>
              <p className="mt-0.5 truncate text-xs text-content-3">
                {telValido(r.phone) ? <span>{r.phone}</span> : <span>Sin teléfono</span>}
                {r.note && <span> · {r.note}</span>}
              </p>
            </div>
            <WaitlistActions row={r} onChanged={recargar} />
          </div>
        ))}
      </div>
    </div>
  );
}
